import store from "../providers/store";

// /******** Environment Variables/Configurations ********/

// const devTeam = ["yle@msi.metrea.aero", "yle@msim.metrea.aero"];
const devSite = ["dev_local"];

/**
 * Determines if debug mode should be enabled based on environment and config LOD.
 *
 * NOTE: use debugLog instead - 8/8/2025
 *
 * @param {number} [level=9] - The debug level threshold (lower is more verbose).
 * @returns {boolean} True if debug output should be shown, false otherwise.
 */
// DEPRECATED - use showLogLevel(level) directly instead
// Prefer using showLogLevel(level) directly.
/*
	const isDebugMode = (level = 9) => {
		// if (showConsole()) return true;
		// return false;

		const configs = JSON.parse(sessionStorage.getItem("configs") || "{}");

		// only display debug info if the level is greater than the Level of Details
		// 1(level): lowest level - display everything, as long as isDebugMode() is called
		// N: only display information for isDebugMode(N) or isDebugMode(N+)
		
		const levelOfDetails = typeof configs.lod !== "undefined" ? parseInt(configs.lod, 10) : 0;
		if (levelOfDetails === -1) return false;
		if (levelOfDetails === 1) return true;

		if (levelOfDetails <= level) {
			// console.log("levelOfDetails", levelOfDetails);
			// console.log("level", level);
		}

		// Only show debug if dev or showConsole enabled and LOD matches
		return (
			// (process.env.NODE_ENV === "development" ? true : false) || (configs && configs.showConsole)
			(process.env.NODE_ENV === "development" || showConsole()) && 1 / levelOfDetails >= 1 / level
			// (process.env.NODE_ENV === "development" || showConsole()) && levelOfDetails <= level
		);
	};
*/
/**
 * Safely read configs state from Redux store.
 * Mirrors getReduxSession() pattern in sessionUtils.
 */
const getReduxConfigs = () => {
	try {
		const state = store.getState();
		return state?.configs || {};
	} catch {
		return {};
	}
};

/**
 * ================================
 * RAVEN DebugLog Quick Cheat Sheet
 * ================================
 *
 * 1) Level 1 -> App-wide events (login, navigation, import/export start)
 * 2) Level 2 -> Page/module entry, major async operations (fetch/save)
 * 3) Level 3 -> User actions + major state changes (handlers, mode changes)
 * 4) Level 4 -> Internal processing (filters, maps, transforms, reducers)
 * 7) Level 7 -> High-frequency handlers (typing/search/input updates)
 * 8) Level 8 -> Very noisy traces (drag/slider/rapid parsed snapshots)
 * 9) Level 9 -> Firehose debugging (render counts, full object dumps)
 *
 * Tag/Level Model:
 * - Tags describe event type: [ACTION], [STATE], [PROCESS], [FLOW], [ERROR].
 * - Level controls verbosity/filter priority.
 *
 * Recommended Defaults:
 * - [FLOW] -> Level 2
 * - [ACTION] -> Level 3
 * - [STATE] -> Level 3
 * - [PROCESS] -> Level 4
 * - High-frequency/noisy traces -> Level 7/8
 * - Firehose traces -> Level 9+
 *
 * Strict Rules:
 * - Always pass ONE object as the details payload.
 * - Use tags consistently for clarity and searchability.
 *
 * LOD Filtering:
 * - lod = -1 -> no logs
 * - lod = 1-9 -> shows levels <= lod
 * - lod >= 10 -> shows levels >= lod
 * - lod = 99 -> show everything
 */

/**
 * =============================================================================
 * RAVEN Debug Logging Standard
 * =============================================================================
 *
 * Centralized debug logging system for all RAVEN modules.
 *
 * All logs MUST use:
 *    debugLog(level, "Message", { detailsObject });
 *
 * -----------------------------------------------------------------------------
 * STRICT FORMAT RULE
 * -----------------------------------------------------------------------------
 *
 * - ALWAYS pass ONE object as the details payload.
 * - NEVER pass loose values.
 *
 * Correct:
 *    debugLog(3, "[BaselineMain][ACTION] handleSave", { id });
 *
 * Incorrect:
 *    debugLog(3, "handleSave", id, data); // INVALID
 *
 *
 * =============================================================================
 * DEBUG LEVEL DEFINITIONS (USE ONLY THESE)
 * =============================================================================
 *
 * LEVEL 1 - App-Level Events & Business Milestones
 * -----------------------------------------------------------------------------
 * Rule: Did something big just happen in the system?
 *
 * Use for:
 *   - Login / logout
 *   - Create / delete major entity
 *   - Import / export started or completed
 *   - Major global system events
 *
 * Examples:
 *   debugLog(1, "User logged in", { userId });
 *   debugLog(1, "Scenario created", { scenarioId });
 *   debugLog(1, "Import started", { fileName });
 *
 *
 * LEVEL 2 - Page / Module Entry & Major Operations
 * -----------------------------------------------------------------------------
 * Rule: Did we enter a module or start a major async operation?
 *
 * Use for:
 *   - Component mounted
 *   - Page navigation
 *   - Major fetch/save batch operations
 *   - Module initialization
 *
 * Examples:
 *   debugLog(2, "[BaselineMain][FLOW] mounted", {});
 *   debugLog(2, "[BaselineService][FLOW] fetch started", {});
 *   debugLog(2, "Users loaded", { count: users.length });
 *
 *
 * LEVEL 3 - User Actions & Major State Transitions
 * -----------------------------------------------------------------------------
 * Rule: What meaningful action or state change just occurred?
 *
 * Use for:
 *   - User-driven handlers (default)
 *   - Button clicks
 *   - Row selections
 *   - Form edits
 *   - Modal open/close
 *   - Important state changes affecting behavior
 *
 * NOTE:
 *   Level 3 is the default for user-triggered handlers.
 *   Use higher levels for high-volume handler logs when noise control is needed.
 *
 * Examples:
 *   debugLog(3, "[BaselineMain][ACTION] handleSave", { id });
 *   debugLog(3, "[ScenarioList][ACTION] handleSelect", { scenarioId });
 *   debugLog(3, "[BaselineMain][STATE] selectedBaseline changed", { selectedBaseline });
 *
 *
 * LEVEL 4 - Internal Processing & Data Transformations
 * -----------------------------------------------------------------------------
 * Rule: How did the system compute this result?
 *
 * Use for:
 *   - Filters
 *   - Maps
 *   - Reducers
 *   - Derived values
 *   - Intermediate calculations
 *
 * Examples:
 *   debugLog(4, "[BaselineMain][PROCESS] filteredItems", { filtered });
 *   debugLog(4, "[ScenarioUtils][PROCESS] mergeConfigs before/after", { before, after });
 *
 * IMPORTANT:
 *   Level 4 is the default for internal system processing.
 *   Use higher levels (for example 7/8) for very noisy processing traces.
 *
 *
 * LEVEL 9 - Deep Debugging / Firehose
 * -----------------------------------------------------------------------------
 * Rule: Show everything.
 *
 * Use for:
 *   - Render counts
 *   - Full object dumps
 *   - Redux state snapshots
 *   - Rare deep debugging sessions
 *
 * Examples:
 *   debugLog(9, "Component render count", { renderCount });
 *   debugLog(9, "Redux snapshot", { state: store.getState() });
 *   debugLog(9, "Full baseline object", { baseline });
 *
 *
 * =============================================================================
 * useEffect CLARIFICATION
 * =============================================================================
 *
 * useEffect does NOT automatically mean Level 4.
 *
 * Ask:
 *
 *   1) Is this reacting to a meaningful state change?
 *         Level 3
 *
 *   2) Is this performing filtering/mapping/calculation?
 *         Level 4
 *
 * Example:
 *
 *   useEffect(() => {
 *     debugLog(3, "[BaselineMain][STATE] selectedBaseline changed", { selectedBaseline });
 *
 *     const filtered = selectedBaseline.items.filter(...);
 *     debugLog(4, "[BaselineMain][PROCESS] filtered items", { filtered });
 *
 *   }, [selectedBaseline]);
 *
 *
 * =============================================================================
 * MESSAGE TAGGING CONVENTION (RECOMMENDED)
 * =============================================================================
 *
 * To improve searchability and clarity, include structured tags in the message:
 *
 * Tag vs Level:
 *   Tags are semantic labels (what happened).
 *   Levels are visibility controls (how often logs should appear).
 *   They are related by convention, not runtime enforcement.
 *
 *   [ACTION]   User-triggered handler
 *   [STATE]    Major state transition
 *   [PROCESS]  Internal transformation/calculation
 *   [FLOW]     Page/module lifecycle or async boundary
 *
 * Examples:
 *
 *   debugLog(3, "[BaselineMain][ACTION] handleSave", { id });
 *   debugLog(3, "[BaselineMain][STATE] selectedBaseline changed", { selectedBaseline });
 *   debugLog(4, "[BaselineMain][PROCESS] filteredItems", { filtered });
 *   debugLog(2, "[BaselineMain][FLOW] mounted", {});
 *
 *
 * =============================================================================
 * LEVEL SELECTION SUMMARY
 * =============================================================================
 *
 * These are recommended defaults, not strict runtime coupling to message tags.
 * Use higher levels (for example 7/8) for high-frequency logs to reduce noise.
 *
 * Level 1 -> Business milestone / app-wide event
 * Level 2 -> Module/page entry or async boundary
 * Level 3 -> User action or major state transition
 * Level 4 -> Internal processing / transformation
 * Level 7 -> High-frequency handlers (typing/search/input)
 * Level 8 -> Very noisy traces (drag/slider/rapid updates)
 * Level 9 -> Firehose / deep debugging
 *
 * FLOW Policy by Layer (RAVEN Standard):
 * - Components: use Level 2 [FLOW] for lifecycle/boundary events.
 * - Redux slices: keep Level 3 [FLOW] for async response milestones.
 * - High-frequency flow chatter: raise to Level 7/8.
 * - Render/deep diagnostics: use Level 9 [FLOW].
 *
 *
 * =============================================================================
 * LOD (Level of Details) FILTERING RULES
 * =============================================================================
 *
 * Based on Redux configs.lod:
 *
 *   lod = -1 -> Logging disabled
 *   lod = 1-9 -> Logs all levels <= lod
 *   lod >=10 -> Logs only levels >= lod
 *   lod = 99 -> Logs everything
 *
 * 
 * =============================================================================
 * HOOKS, SLICES, AND UTILS CLARIFICATION
 * =============================================================================
 *
 * Hooks:
 *   - Levels listed below are defaults; raise level for high-frequency logs.
 *   - Exposed functions called by components -> Level 3 [ACTION]
 *   - Async boundaries (fetch/save start) -> Level 2 [FLOW]
 *   - Internal transforms / derived state -> Level 4 [PROCESS]
 *
 * Redux Slices:
 *   - Async thunks (API entry) -> Level 3
 *   - Reducer internal state transforms -> Level 4
 *
 * Utils:
 *   - Pure data transformations -> Level 4 [PROCESS]
 *   - Rare business-rule boundary decisions -> Level 3 [STATE]
 *
 * 
 * =============================================================================
 * PARAMETERS
 * =============================================================================
 *
 * @param {number} level   - Debug level (see definitions above).
 * @param {string} message - Log message (component-tag recommended).
 * @param {object} details - Single object payload (REQUIRED).
 *
 * @returns {boolean} Whether the log should be shown.
 * =============================================================================
 */

/**
 * Determines if a log message for the given level should be shown, based on the configured debug level (lod).
 * @param {number} level - The debug level of the message to check.
 * @returns {boolean} True if the message should be shown, false otherwise.
 */
// export const showLogLevel = (level = 1) => {
// 	const configs = JSON.parse(sessionStorage.getItem("configs") || "{}");
// 	const levelOfDetails = typeof configs.lod !== "undefined" ? parseInt(configs.lod, 10) : 0;

// 	// Show nothing if disabled
// 	if (levelOfDetails === -1) return false;

// 	if (levelOfDetails === 99) return true;
// 	else if (levelOfDetails >= 10) return level >= levelOfDetails;
// 	else return levelOfDetails >= level;
// };
export const showLogLevel = (level = 1) => {
	const { lod = 0 } = getReduxConfigs();

	// Disabled
	if (lod === -1) return false;

	// Firehose
	if (lod === 99) return true;

	// Experimental / custom high levels
	if (lod >= 10) return level >= lod;

	// Standard inclusive logic
	return lod >= level;
};

/**
 * Logs messages conditionally based on debug level and always as a single object.
 * @param {number} level - Debug level.
 * @param {string} message - Log description/tag.
 * @param {object} details - Log data, always as an object.
 *
 * @example
 *   debugLog(2, "[BaselineList] mounted", { listCount: list.length });
 *   debugLog(4, "[BaselineList] handleEdit", { id: id });
 */
export const debugLog = (level = 1, message = "", details = {}) => {
	if (showLogLevel(level)) {
		const ICONS = {
            1: "[L1]",
            2: "[L2]",
            3: "[L3]",
            4: "[L4]",
            7: "[L7]",
            8: "[L8]",
            9: "[L9]",
            10: "[L10]", // experimental/custom levels
            99: "[ALL]", // firehose/all logs
        };
        const COLORS = {
            1: "color: #008000; font-weight: bold", // green
            2: "color: #1e90ff; font-weight: bold", // blue
            3: "color: #ff9800; font-weight: bold", // orange
            4: "color: #8e24aa; font-weight: bold", // purple
            7: "color: #6d4c41; font-weight: bold", // brown
            8: "color: #455a64; font-weight: bold", // blue-gray
            9: "color: #d32f2f; font-weight: bold", // red
            10: "color: #ffd700; font-weight: bold", // gold/yellow (star)
            99: "background: linear-gradient(90deg, red, orange, yellow, green, blue, indigo, violet); color: white; font-weight: bold; padding: 2px 8px; border-radius: 5px;",
        };

		// Pick the icon and color for the exact level, or fallback for 10+ and 99
		let icon = ICONS[level];
		let color = COLORS[level];
		if (!icon) {
			if (level >= 10 && level < 99) {
				icon = ICONS[10];
				color = COLORS[10];
			} else if (level >= 99) {
				icon = ICONS[99];
				color = COLORS[99];
			} else {
				icon = "[DBG]";
				color = "color: #333";
			}
		}

		// Caller extraction is intentionally omitted here to keep debug logs cheap
		// in the common case and avoid unused-stack lint noise.
		// console.log(`%c${icon} DEBUG-${level}:`, color, ...args);

		console.groupCollapsed(`%c${icon} DEBUG-${level}: ${message}`, color);
		console.log(details);
		console.groupEnd();
	}
};

export const errorLog = (level = 1, message = "", details = {}) => {
	if (showLogLevel(level)) {
		console.error(`ERROR-${level}: ${message}`, details);
	}
};

/**
 * Checks if console output should be shown based on environment or config.
 * @returns {boolean} True if console output should be shown.
 */
export const showConsole = () => {
	const { showConsole } = getReduxConfigs();
	return showConsole !== false; // Default = true
};

/**
 * Checks if testing features should be enabled.
 * @returns {boolean} True if testing features should be shown.
 */
export const showTesting = () => {
	const { showTesting } = getReduxConfigs();
	return showTesting === true;
};

/**
 * Determines if the current site is a development site.
 * @param {object} [env=process.env] - The environment object to check.
 * @returns {boolean} True if the site is in the devSite list.
 */
export const isDevSiteNow = (env = process.env) => {
	return devSite.includes(env.REACT_APP_ENV);
};
